import { ErrorAlert } from "#components/ErrorAlert"
import { Alert, AlertDescription, AlertTitle } from "#components/ui/alert"
import { Button } from "#components/ui/button"
import { Checkbox } from "#components/ui/checkbox"
import { Input } from "#components/ui/input"
import { Label } from "#components/ui/label"
import { useI18n } from "#hooks/use-i18n"
import { useCreateInvitation } from "#hooks/use-invitations"
import { fadeIn, slideUp } from "#lib/animations"
import { getUserFriendlyErrorMessage } from "#lib/errors"
import type { Account } from "@home-finance-manager/shared/types/accounts"
import { createCreateInvitationValidator } from "@home-finance-manager/shared/validators/invitations.validator.factory"
import { zodResolver } from "@hookform/resolvers/zod"
import { AnimatePresence, motion } from "framer-motion"
import { CheckCircle, X } from "lucide-react"
import { useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"

interface InviteUserFormProps {
  accounts: Account[]
}

export function InviteUserForm({ accounts }: InviteUserFormProps) {
  const { t } = useI18n()
  const createInvitation = useCreateInvitation()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const inviteSchema = useMemo(() => createCreateInvitationValidator(t), [t])
  type InviteFormData = z.input<typeof inviteSchema>

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: "",
      accountIds: [],
      role: "member" as const,
    },
  })

  const onSubmit = async (data: InviteFormData) => {
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      await createInvitation.mutateAsync({
        email: data.email,
        accountIds: data.accountIds,
        role: data.role || "member",
      })
      setSubmitSuccess(`Invitation sent successfully to ${data.email}`)
      reset()
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(null)
      }, 5000)
    } catch (error) {
      setSubmitError(getUserFriendlyErrorMessage(error))
    }
  }

  return (
    <motion.form
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
    >
      <motion.div variants={slideUp} className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              {...field}
              className={errors.email ? "border-red-500" : ""}
            />
          )}
        />
        <AnimatePresence>
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-sm text-red-500"
            >
              {errors.email.message}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div variants={slideUp} className="space-y-2">
        <Label>Select Accounts</Label>
        <div className="space-y-2">
          {accounts.map((account, index) => (
            <motion.div
              key={account.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Controller
                name="accountIds"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`account-${account.id}`}
                      checked={field.value?.includes(account.id)}
                      onCheckedChange={(checked) => {
                        const current = field.value || []
                        if (checked) {
                          field.onChange([...current, account.id])
                        } else {
                          field.onChange(current.filter((id: number) => id !== account.id))
                        }
                      }}
                    />
                    <Label
                      htmlFor={`account-${account.id}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {account.name}
                    </Label>
                  </div>
                )}
              />
            </motion.div>
          ))}
        </div>
        <AnimatePresence>
          {errors.accountIds && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-sm text-red-500"
            >
              {errors.accountIds.message}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div variants={slideUp} className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <select
              id="role"
              {...field}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          )}
        />
        <AnimatePresence>
          {errors.role && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-sm text-red-500"
            >
              {errors.role.message}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>

      <ErrorAlert error={submitError} onDismiss={() => setSubmitError(null)} />

      <AnimatePresence>
        {submitSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="mb-4"
          >
            <Alert className="relative border-green-500/50 bg-green-500/10">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-500" />
              <AlertTitle className="text-green-800 dark:text-green-200">Success</AlertTitle>
              <AlertDescription className="pr-8 text-green-700 dark:text-green-300">
                {submitSuccess}
              </AlertDescription>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="absolute top-3 right-3"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-full hover:bg-green-500/20"
                  onClick={() => setSubmitSuccess(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </motion.div>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div variants={slideUp}>
        <Button type="submit" disabled={createInvitation.isPending}>
          {createInvitation.isPending
            ? t("components.settings.invitation.sending")
            : t("components.settings.invitation.sendInvitation")}
        </Button>
      </motion.div>
    </motion.form>
  )
}
